import type { RestPeriodImpactPreview,RestPeriodImpactPreviewContribution } from "./RestPeriodImpactPreviewCard";
import { CONTRIBUTION_STATUS_CANCELLED } from "../../app/constants/contributionCalendarStatus";
import { ISO_DATE_LENGTH } from "../../app/utils/isoDate";

/** Abweichungen unterhalb eines halben Cent gelten als keine Änderung. */
const MONEY_ROUNDING_TOLERANCE = 0.005;

export type PreviewContributionItem = RestPeriodImpactPreviewContribution & {
  changeKind: "new" | "changed" | "removed" | "unchanged";
  before?: RestPeriodImpactPreviewContribution | null;
};


function contributionCompareKey(contribution: RestPeriodImpactPreviewContribution): string {
  // Derselbe Termin kann Original, importierte Korrektur und Gebühr enthalten.
  // Nur die Datensatzkennung ordnet die Vorher-/Nachher-Zeile eindeutig zu.
  return contribution.id;
}

function contributionChanged(
  before: RestPeriodImpactPreviewContribution,
  after: RestPeriodImpactPreviewContribution
): boolean {
  return Number(before.amount || 0) !== Number(after.amount || 0)
    || Number(before.paidAmount || 0) !== Number(after.paidAmount || 0)
    || String(before.dueDate || "") !== String(after.dueDate || "")
    || String(before.contractName || "") !== String(after.contractName || "")
    || String(before.info || "") !== String(after.info || "")
    || Number(before.openAmount || 0) !== Number(after.openAmount || 0)
    || Number(before.status || 0) !== Number(after.status || 0)
    || String(before.periodStart || "") !== String(after.periodStart || "")
    || String(before.periodEnd || "") !== String(after.periodEnd || "")
    || String(before.periodDescription || "") !== String(after.periodDescription || "");
}

export function buildPreviewContributionItems(preview: RestPeriodImpactPreview): PreviewContributionItem[] {
  const beforeByKey = new Map(preview.currentContributions.map((contribution) => [contributionCompareKey(contribution), contribution]));
  const afterByKey = new Map(preview.previewContributions.map((contribution) => [contributionCompareKey(contribution), contribution]));
  const items: PreviewContributionItem[] = [];

  for (const after of preview.previewContributions) {
    const key = contributionCompareKey(after);
    const before = beforeByKey.get(key) || null;
    items.push({
      ...after,
      before,
      changeKind: before ? (contributionChanged(before, after) ? "changed" : "unchanged") : "new"
    });
  }

  for (const before of preview.currentContributions) {
    const key = contributionCompareKey(before);
    if (afterByKey.has(key)) continue;
    items.push({
      ...before,
      before,
      changeKind: "removed"
    });
  }

  return items.sort((a, b) => String(a.dueDate || "").localeCompare(String(b.dueDate || "")));
}


export function isRestPeriodFeeContribution(contribution: RestPeriodImpactPreviewContribution): boolean {
  return String(contribution.info || "").trim().toLowerCase().startsWith("ruhezeit:");
}

export type RestPeriodImpactSummary = {
  /** Beiträge, die wegfallen oder gemindert werden (gestrichen, storniert, Storno-Beleg, Betrag gesenkt). */
  suspendedCount: number;
  suspendedAmount: number;
  /** Neue oder geänderte Ruhezeitgebühren nach dem Speichern. */
  feeAmount: number;
};

function effectiveAmount(contribution: RestPeriodImpactPreviewContribution | null | undefined): number {
  if (!contribution || contribution.status === CONTRIBUTION_STATUS_CANCELLED) return 0;
  return Number(contribution.amount || 0);
}

/**
 * Kurzfassung für die Kopfzeile der Folgenvorschau; die Einzelzeilen bleiben darunter.
 * Gerechnet wird netto je Beitragszeitraum, damit Original und Storno-Beleg einer Woche
 * nicht doppelt zählen.
 */
export function summarizeRestPeriodImpact(items: PreviewContributionItem[]): RestPeriodImpactSummary {
  const deltaByPeriod = new Map<string, number>();
  let feeAmount = 0;
  for (const item of items) {
    const before = item.changeKind === "new" ? 0 : effectiveAmount(item.before);
    const after = item.changeKind === "removed" ? 0 : effectiveAmount(item);
    if (isRestPeriodFeeContribution(item)) {
      feeAmount += after - before;
      continue;
    }
    if (after === before) continue;
    const period = `${item.contractId ?? ""}|${String(item.periodStart || item.dueDate || "").slice(0, ISO_DATE_LENGTH)}`;
    deltaByPeriod.set(period, (deltaByPeriod.get(period) ?? 0) + after - before);
  }
  let suspendedCount = 0;
  let suspendedAmount = 0;
  for (const delta of deltaByPeriod.values()) {
    if (delta >= -MONEY_ROUNDING_TOLERANCE) continue;
    suspendedCount += 1;
    suspendedAmount += -delta;
  }
  return { suspendedCount, suspendedAmount, feeAmount };
}
